import api from './api';
import { unwrapObject } from './api-envelope';
import type { RebateCalculationPeriod } from './b2b-rebate-policy.service';

export type B2BRebateLedgerBucket =
  | 'OPEN_ACCRUAL'
  | 'PENDING_SETTLEMENT'
  | 'SETTLED'
  | 'BLOCKED'
  | 'BELOW_TIER'
  | 'OTHER';

export interface B2BRebateLedgerBalances {
  earned_amount: number;
  settled_amount: number;
  pending_amount: number;
  open_amount: number;
  remaining_amount: number;
  remaining_to_settle_amount?: number;
  available_to_apply_amount?: number;
  applied_to_orders_amount: number;
  reserved_on_orders_amount?: number;
  movement_count: number;
  brand_count: number;
}

export interface B2BRebateLedgerCustomer {
  customer_id: string;
  customer_code: string;
  company_name: string;
  tax_code: string;
  salesperson_name: string;
  manual_wallet_amount?: number;
  /** Số dư ví hiện tại. Không cắt theo khoảng ngày của sổ. */
  wallet_available_amount?: number;
  balances: B2BRebateLedgerBalances;
  brands: Array<{
    brand_id: string | null;
    brand_name: string;
    balances: B2BRebateLedgerBalances;
  }>;
}

export interface B2BRebateLedgerMovement {
  id: string;
  source: 'ACCRUAL' | 'SETTLEMENT';
  accrual_id: string | null;
  settlement_id: string | null;
  settlement_code: string | null;
  policy_id: string | null;
  policy_name: string;
  brand_id: string | null;
  brand_name: string;
  period_key: string;
  calculation_period: RebateCalculationPeriod;
  period_start: string;
  period_end: string;
  revenue_net: number;
  rebate_percent: number;
  amount: number;
  status: string;
  bucket: B2BRebateLedgerBucket;
  order_count: number;
  source_order_ids: string[];
  settlement_reference: string;
  salesperson_name: string;
  calculated_at: string;
  settled_at: string | null;
}

export interface B2BRebateLedgerQuery {
  history_debit_page?: number;
  history_credit_page?: number;
  history_manual_credit_page?: number;
  history_page_size?: number;
  date_from?: string;
  date_to?: string;
  search?: string;
  brand_id?: string;
  page?: number;
  page_size?: number;
}

export interface B2BRebateLedgerList {
  items: B2BRebateLedgerCustomer[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  summary: {
    customer_count: number;
    earned_amount: number;
    settled_amount: number;
    pending_amount: number;
    open_amount: number;
    remaining_amount: number;
    remaining_to_settle_amount?: number;
    available_to_apply_amount?: number;
    wallet_available_amount?: number;
    applied_to_orders_amount: number;
    reserved_on_orders_amount?: number;
    period_start: string;
    period_end: string;
    note: string;
  };
}

export interface B2BRebateLedgerApplication {
  id: string;
  order_id: string | null;
  order_number: string;
  method?: 'ORDER_OFFSET' | 'CASH' | 'AR_OFFSET' | 'OTHER';
  voucher_ref?: string;
  paid_at?: string | null;
  amount: number;
  status: string;
  note: string;
  reserved_at?: string | null;
  applied_at: string | null;
  released_at?: string | null;
  reversed_at: string | null;
  reverse_reason?: string;
  allocations: Array<{
    settlement_id: string;
    settlement_code: string;
    amount: number;
  }>;
}

export interface B2BRebateLedgerDetail {
  customer: B2BRebateLedgerCustomer;
  movements: B2BRebateLedgerMovement[];
  applications?: B2BRebateLedgerApplication[];
  wallet?: {
    credited_amount: number;
    spent_amount: number;
    reserved_amount: number;
    available_amount: number;
  };
  wallet_credits?: B2BRebateWalletCredit[];
  wallet_settlement_credits?: B2BRebateLedgerMovement[];
  history_pagination?: Record<'debit' | 'credit' | 'manual_credit', {
    page: number;
    page_size: number;
    total: number;
  }>;
  summary: B2BRebateLedgerBalances & {
    period_start: string;
    period_end: string;
    note: string;
  };
}

export interface B2BRebateCredit {
  customer_id: string;
  settled_amount: number;
  applied_amount: number;
  reserved_amount?: number;
  /** Khoản RESERVED của chính đơn đang sửa, đã được cộng lại vào available_amount. */
  current_order_reserved_amount?: number;
  available_amount: number;
  available_settlements?: Array<{
    _id: string;
    settlement_code: string;
    amount: number;
    period_key: string;
    brand_name: string;
  }>;
  note: string;
}

export type B2BRebateWalletDisburseMethod = 'CASH' | 'AR_OFFSET';

export interface B2BRebateWalletDisburseBody {
  customer_id: string;
  amount: number;
  method: B2BRebateWalletDisburseMethod;
  voucher_ref?: string;
  paid_at?: string;
  note?: string;
  company_name?: string;
  customer_code?: string;
  idempotency_key?: string;
}

export interface B2BRebateWalletCredit {
  id: string;
  amount: number;
  voucher_ref: string;
  actor_id?: string | null;
  actor_name?: string;
  credited_at: string;
  note: string;
}

export interface B2BRebateWalletCreditBody {
  customer_id: string;
  amount: number;
  voucher_ref?: string;
  credited_at?: string;
  note: string;
  idempotency_key?: string;
}

export type B2BRebateWalletImportRowStatus = 'ok' | 'error' | 'skipped';

export interface B2BRebateWalletImportRow {
  row: number;
  customer_code: string;
  company_name: string;
  amount: number | null;
  status: B2BRebateWalletImportRowStatus;
  message: string;
  application_id?: string;
  movement_id?: string;
  voucher_ref?: string;
  method?: string;
  occurred_at?: string;
  note?: string;
  balance_before?: number;
  balance_after?: number;
}

export interface B2BRebateWalletImportResult {
  total_rows: number;
  success: number;
  failed: number;
  skipped: number;
  rows: B2BRebateWalletImportRow[];
}

export interface B2BRebateWalletImportConfirmation {
  preview_id: string;
  preview_hash: string;
}

export interface B2BRebateWalletImportPreview extends B2BRebateWalletImportResult, B2BRebateWalletImportConfirmation {
  total_amount: number;
}

export const b2bRebateLedgerService = {
  list: (params: B2BRebateLedgerQuery): Promise<B2BRebateLedgerList> =>
    api.get('/b2b/rebate-ledger', { params }).then(unwrapObject<B2BRebateLedgerList>),

  detail: (
    customerId: string,
    params: B2BRebateLedgerQuery,
  ): Promise<B2BRebateLedgerDetail> =>
    api
      .get(`/b2b/rebate-ledger/${customerId}`, { params })
      .then(unwrapObject<B2BRebateLedgerDetail>),

  credit: (customerId: string, orderId?: string): Promise<B2BRebateCredit> =>
    api
      .get(`/b2b/rebate-ledger/credit/${customerId}`, {
        params: orderId ? { order_id: orderId } : undefined,
      })
      .then(unwrapObject<B2BRebateCredit>),

  disburse: (body: B2BRebateWalletDisburseBody) =>
    api
      .post('/b2b/rebate-ledger/wallet/disburse', body)
      .then(unwrapObject<Record<string, unknown>>),

  creditWallet: (body: B2BRebateWalletCreditBody) =>
    api
      .post('/b2b/rebate-ledger/wallet/credit', body)
      .then(unwrapObject<Record<string, unknown>>),

  reverseDisburse: (id: string, body: { reason: string }) =>
    api
      .post(`/b2b/rebate-ledger/wallet/${id}/reverse`, body)
      .then(unwrapObject<Record<string, unknown>>),

  downloadDisburseTemplate: (): Promise<Blob> =>
    api
      .get<Blob>('/b2b/rebate-ledger/wallet/disburse-template', {
        responseType: 'blob',
      })
      .then((response) => response.data),

  downloadCreditTemplate: (): Promise<Blob> =>
    api.get<Blob>('/b2b/rebate-ledger/wallet/credit-template', { responseType: 'blob' })
      .then((response) => response.data),

  importCredit: (
    file: File,
    confirmation: B2BRebateWalletImportConfirmation,
    onUploadProgress?: (percent: number) => void,
  ): Promise<B2BRebateWalletImportResult> => {
    const form = new FormData();
    form.append('file', file);
    form.append('preview_id', confirmation.preview_id);
    form.append('preview_hash', confirmation.preview_hash);
    return api.post('/b2b/rebate-ledger/wallet/credit-import', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120_000,
      onUploadProgress: (event) => {
        if (onUploadProgress && event.total) {
          onUploadProgress(Math.round((event.loaded / event.total) * 30));
        }
      },
    }).then(unwrapObject<B2BRebateWalletImportResult>);
  },

  importDisburse: (
    file: File,
    confirmation: B2BRebateWalletImportConfirmation,
    onUploadProgress?: (percent: number) => void,
  ): Promise<B2BRebateWalletImportResult> => {
    const form = new FormData();
    form.append('file', file);
    form.append('preview_id', confirmation.preview_id);
    form.append('preview_hash', confirmation.preview_hash);
    return api
      .post('/b2b/rebate-ledger/wallet/disburse-import', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120_000,
        onUploadProgress: (event) => {
          if (!onUploadProgress || !event.total) return;
          onUploadProgress(Math.round((event.loaded / event.total) * 30));
        },
      })
      .then(unwrapObject<B2BRebateWalletImportResult>);
  },

  previewWalletImport: (file: File, mode: 'credit' | 'disburse', previewId?: string): Promise<B2BRebateWalletImportPreview> => {
    const form = new FormData();
    form.append('file', file);
    if (previewId) form.append('preview_id', previewId);
    return api.post(`/b2b/rebate-ledger/wallet/${mode}-import-preview`, form, {
      headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120_000,
    }).then(unwrapObject<B2BRebateWalletImportPreview>);
  },
};
